import {
	ActionIcon,
	Badge,
	Group,
	Table,
	Text,
	UnstyledButton,
	Center,
} from "@mantine/core";
import {
	IconEdit,
	IconToggleLeft,
	IconToggleRight,
	IconTrash,
	IconChevronUp,
	IconChevronDown,
	IconSelector,
} from "@tabler/icons-react";
import type { User } from "../../store/users/usersSlice";

export type SortDirection = "asc" | "desc" | null;
export type UserSortField =
	| "username"
	| "email"
	| "role"
	| "status"
	| "createdAt"
	| null;

interface ThSortableProps {
	children: React.ReactNode;
	sorted: boolean;
	reversed: boolean;
	onSort: () => void;
}

function ThSortable({ children, sorted, reversed, onSort }: ThSortableProps) {
	const Icon = sorted
		? reversed
			? IconChevronUp
			: IconChevronDown
		: IconSelector;
	return (
		<Table.Th>
			<UnstyledButton
				onClick={onSort}
				style={{
					width: "100%",
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					padding: "4px 0",
					cursor: "pointer",
				}}>
				<Text
					fw={500}
					size="sm">
					{children}
				</Text>
				<Center style={{ width: 21, height: 21 }}>
					<Icon
						size={16}
						stroke={1.5}
					/>
				</Center>
			</UnstyledButton>
		</Table.Th>
	);
}

interface UserTableProps {
	users: User[];
	currentUserId?: string;
	currentUserRole?: string;
	sortField?: UserSortField;
	sortDirection?: SortDirection;
	onSort?: (field: UserSortField) => void;
	onToggleUser: (user: User) => void;
	onEditUser: (user: User) => void;
	onDeleteUser: (id: string, username: string) => void;
}

function UserTable({
	users,
	currentUserId,
	currentUserRole,
	sortField,
	sortDirection,
	onSort,
	onToggleUser,
	onEditUser,
	onDeleteUser,
}: UserTableProps) {
	const canModifyUser = (user: User) => {
		return currentUserRole === "admin" && user._id !== currentUserId;
	};

	const handleSort = (field: UserSortField) => {
		if (onSort) {
			onSort(field);
		}
	};

	return (
		<Table.ScrollContainer
			minWidth={500}
			maxHeight={500}>
			<Table
				striped
				highlightOnHover>
				<Table.Thead>
					<Table.Tr>
						<ThSortable
							sorted={sortField === "username"}
							reversed={sortDirection === "asc"}
							onSort={() => handleSort("username")}>
							Username
						</ThSortable>
						<ThSortable
							sorted={sortField === "email"}
							reversed={sortDirection === "asc"}
							onSort={() => handleSort("email")}>
							Email
						</ThSortable>
						<ThSortable
							sorted={sortField === "role"}
							reversed={sortDirection === "asc"}
							onSort={() => handleSort("role")}>
							Role
						</ThSortable>
						<ThSortable
							sorted={sortField === "status"}
							reversed={sortDirection === "asc"}
							onSort={() => handleSort("status")}>
							Status
						</ThSortable>
						<ThSortable
							sorted={sortField === "createdAt"}
							reversed={sortDirection === "asc"}
							onSort={() => handleSort("createdAt")}>
							Created
						</ThSortable>
						<Table.Th>Actions</Table.Th>
					</Table.Tr>
				</Table.Thead>
				<Table.Tbody>
					{users.map((user) => (
						<Table.Tr key={user._id}>
							<Table.Td>
								<div>
									<Text fw={500}>{user.username}</Text>
									{user._id === currentUserId && (
										<Badge
											size="xs"
											color="blue"
											variant="light">
											You
										</Badge>
									)}
								</div>
							</Table.Td>
							<Table.Td>{user.email}</Table.Td>
							<Table.Td>
								<Badge
									variant="light"
									color={
										user.role === "admin"
											? "violet"
											: user.role === "manager"
											? "blue"
											: "gray"
									}>
									{user.role.charAt(0).toUpperCase() + user.role.slice(1)}
								</Badge>
							</Table.Td>
							<Table.Td>
								<Badge
									color={user.isActive ? "green" : "red"}
									variant="light">
									{user.isActive ? "Active" : "Inactive"}
								</Badge>
							</Table.Td>
							<Table.Td>
								<Text size="sm">
									{new Date(user.createdAt).toLocaleDateString()}
								</Text>
							</Table.Td>
							<Table.Td>
								<Group gap="xs">
									{currentUserRole === "admin" && (
										<>
											{canModifyUser(user) && (
												<ActionIcon
													variant="light"
													color={user.isActive ? "orange" : "green"}
													onClick={() => onToggleUser(user)}>
													{user.isActive ? (
														<IconToggleRight size={16} />
													) : (
														<IconToggleLeft size={16} />
													)}
												</ActionIcon>
											)}
											<ActionIcon
												variant="light"
												color="blue"
												onClick={() => onEditUser(user)}>
												<IconEdit size={16} />
											</ActionIcon>
											{canModifyUser(user) && (
												<ActionIcon
													variant="light"
													color="red"
													onClick={() => onDeleteUser(user._id, user.username)}>
													<IconTrash size={16} />
												</ActionIcon>
											)}
										</>
									)}
								</Group>
							</Table.Td>
						</Table.Tr>
					))}
				</Table.Tbody>
			</Table>
		</Table.ScrollContainer>
	);
}

export default UserTable;
